import {
    type DateRange,
    GetBalancesParams,
    type TimeBalance,
    formatDate,
    formatRange,
    getCurrentMonthRange,
    getRange,
    toDateString,
    toDurationString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, type TemplateResult, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { popover } from "../../../directives/popover";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import "../../../elements/popover";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/recalcuate-results-dialog";
import type { RecalculateResultsDialog } from "../components/recalcuate-results-dialog";
import "../components/reset-balances-dialog";
import type { ResetBalancesDialog } from "../components/reset-balances-dialog";

@customElement("ptc-employees-time-balance-single")
export class EmployeesTimeBalanceSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<employeeId>\d+)\/time-balance$/;

    @routeProperty({ arg: "employeeId", type: Number })
    employeeId: number;

    defaultRange = () => {
        const firstContract = this._employee?.firstContract;
        if (!firstContract) {
            return getCurrentMonthRange();
        }
        const from = getRange(firstContract.start, this._resolution || "month").from;
        const today = toDateString(new Date());
        const contractEnd = this._employee?.latestContract?.inclusiveEnd;
        let to = !contractEnd || today < contractEnd ? today : contractEnd;
        to = getRange(to, this._resolution || "month").to;
        return { from, to };
    };

    @routeProperty({ param: "resolution", default: () => "month" })
    private _resolution: "month" | "week" | "year";

    @singleton("ptc-reset-balances-dialog")
    private _resetBalancesDialog: ResetBalancesDialog;

    @singleton("ptc-recalculate-results-dialog")
    private _recalculateResultsDialog: RecalculateResultsDialog;

    get routeTitle() {
        return `Arbeitszeitkonto: ${this._employee?.name}`;
    }

    @state()
    private _balances: TimeBalance[] = [];

    @state()
    private _loading = false;

    private get _employee() {
        return app.getEmployee(this.employeeId);
    }

    updated(changes: Map<string, unknown>) {
        if (
            changes.has("employeeId") ||
            changes.has("dateFrom") ||
            changes.has("dateTo") ||
            changes.has("_resolution")
        ) {
            // update in the background
            void this.load();
        }
    }

    async load() {
        if (this._loading || !this._resolution) {
            return;
        }

        this._loading = true;

        try {
            const resolution = this._resolution;
            this._balances = await app.api.getTimeBalances(
                new GetBalancesParams({
                    from: this.dateFrom,
                    to: this.dateTo,
                    resolution,
                    filters: [{ type: "employeeId", value: this.employeeId }],
                })
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _editBalancesReset(balance: TimeBalance) {
        const edited = await this._resetBalancesDialog.show({
            date: balance.from,
            filters: [{ type: "employeeId", value: balance.employeeId }],
        });

        if (edited) {
            // update in the background
            void this.load();
        }
    }

    private async _resetBalances() {
        const edited = await this._resetBalancesDialog.show({
            date: getRange(new Date(), this._resolution || "month").from,
            filters: [{ type: "employeeId", value: this._employee!.id }],
        });

        if (edited) {
            // update in the background
            void this.load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            table {
                border-collapse: separate;
                display: block;
            }

            thead {
                display: block;
                position: sticky;
                top: 0;
                z-index: 2;
            }

            tbody {
                display: block;
                margin-bottom: 1em;
                box-sizing: border-box;
            }

            tr {
                display: grid;
                grid-template-columns: var(--column-width-date) repeat(5, 8rem);
                align-items: center;
                width: 100%;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
            }

            td {
                text-align: right;
            }

            thead {
                font-weight: bold;
                background: var(--color-bg);
                text-align: center;
            }

            tr.subbalance > th {
                padding-left: 2em;
            }
        `,
    ];

    private _renderBalances(balances: TimeBalance[], sub = false): TemplateResult {
        balances.sort((a, b) => (a.from > b.from ? -1 : 1));
        return html`
            ${balances.map(
                (balance, i) => html`
                    <tr
                        class="click ${sub ? "smaller subbalance" : ""}"
                        @click=${() =>
                            this.go(`employees/${this.employeeId}/time`, {
                                from: balance.from,
                                to: balance.to,
                            })}
                    >
                        <th>${formatRange(balance)}</th>
                        <td>
                            <ptc-ui-balance
                                .value=${balance.reset ?? balance.carry}
                                .parser=${(val: number) => toDurationString(val, true)}
                            ></ptc-ui-balance>
                        </td>
                        <td>${toDurationString(balance.nominal)}</td>
                        <td>
                            ${balance.adjustments || balance.absences
                                ? html`<sup
                                      class="tinier"
                                      ${popover(
                                          html`
                                              <table class="simple">
                                                  <thead>
                                                      <tr>
                                                          <th style="text-align: left;">Ursache</th>
                                                          <th>Stunden</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody>
                                                      <tr>
                                                          <th>
                                                              <ptc-ui-icon name="icon.time-entry.work"></ptc-ui-icon>
                                                              Gearbeitet
                                                          </th>
                                                          <td>${toDurationString(balance.work)}</td>
                                                      </tr>
                                                      ${balance.absences
                                                          ? html`
                                                                <tr>
                                                                    <th>
                                                                        <ptc-ui-icon
                                                                            name="icon.object.absence"
                                                                        ></ptc-ui-icon>
                                                                        Urlaub / Krank
                                                                    </th>
                                                                    <td>${toDurationString(balance.absences)}</td>
                                                                </tr>
                                                            `
                                                          : ""}
                                                      ${balance.adjustments
                                                          ? html`
                                                                <tr>
                                                                    <th>
                                                                        <ptc-ui-icon
                                                                            name="icon.object.booking"
                                                                        ></ptc-ui-icon>
                                                                        Zeitbuchungen
                                                                    </th>
                                                                    <td>${toDurationString(balance.adjustments)}</td>
                                                                </tr>
                                                            `
                                                          : ""}
                                                  </tbody>
                                              </table>
                                          `,
                                          {
                                              trigger: "hover",
                                              class: "text-left-aligning tooltip non-interactive",
                                          }
                                      )}
                                  >
                                      ${balance.absences
                                          ? html`<ptc-ui-icon name="icon.object.absence"></ptc-ui-icon>`
                                          : ""}${balance.adjustments
                                          ? html`<ptc-ui-icon name="icon.object.booking"></ptc-ui-icon>`
                                          : ""}
                                  </sup> `
                                : ""}
                            ${toDurationString(balance.actual)}
                        </td>
                        <td>
                            <ptc-ui-balance
                                .value=${balance.difference}
                                .iconStyle=${"up-down"}
                                class="semibold"
                                .parser=${(val: number) => toDurationString(val, true)}
                            ></ptc-ui-balance>
                        </td>
                        <td>
                            <ptc-ui-balance
                                class="bold"
                                .value=${balance.balance}
                                .parser=${(val: number) => toDurationString(val, true)}
                            ></ptc-ui-balance>
                        </td>
                    </tr>

                    ${typeof balance.subBalances !== "undefined" ? this._renderBalances(balance.subBalances, true) : ""}
                    ${typeof balance.reset === "number" && (!sub || i < balances.length - 1)
                        ? html`
                              <tr class="click subbalance" @click=${() => this._editBalancesReset(balance)}>
                                  <th class="bold smaller" style="grid-column: span 3;">
                                      <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konto zurückgesetzt am
                                      ${formatDate(balance.from)}
                                  </th>
                                  <td class="smaller" style="grid-column: span 3;">
                                      <ptc-ui-balance
                                          .value=${balance.carry}
                                          .parser=${(val: number) => toDurationString(val, true)}
                                          class="semibold"
                                      ></ptc-ui-balance>
                                      <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                      <ptc-ui-balance
                                          .value=${balance.reset}
                                          class="bold"
                                          .parser=${(val: number) => toDurationString(val, true)}
                                      ></ptc-ui-balance>
                                  </td>
                              </tr>
                          `
                        : ""}
                `
            )}
        `;
    }

    render() {
        return html`
            <div class="center-aligning horizontal layout padded border-bottom">
                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) =>
                        this.go(null, { ...this.router.params, ...e.detail })}
                    .range=${this.dateRange}
                ></ptc-date-range-picker>

                <div class="left-margined">Auflösung:</div>

                <select
                    class="slim transparent bold"
                    .value=${live(this._resolution)}
                    @change=${(e: Event) =>
                        this.go(null, { ...this.router.params, resolution: (e.target as HTMLSelectElement).value })}
                >
                    <option .value=${"week"}>Woche</option>
                    <option .value=${"month"}>Monat</option>
                    <option .value=${"year"}>Jahr</option>
                </select>

                <div class="stretch"></div>

                ${app.hasPermission("manage.exports.ledgers_time")
                    ? html`<button
                          class="transparent slim right-margined"
                          title="Arbeitszeiten Exportieren"
                          @click=${() =>
                              this.go("exports/balances-time-excel", {
                                  from: this.dateFrom,
                                  to: this.dateTo,
                                  "entity-filters": JSON.stringify([{ type: "employeeId", value: this.employeeId }]),
                              })}
                      >
                          <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                      </button>`
                    : nothing}

                <button class="slim transparent">
                    <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                </button>

                <ptc-popover class="popover-menu">
                    <button @click=${this._resetBalances}>
                        <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konten Zurücksetzen
                    </button>
                    <button
                        @click=${async () => {
                            const recalculated = await this._recalculateResultsDialog.show({
                                filters: [{ type: "employeeId", value: this._employee!.id }],
                            });
                            if (recalculated) {
                                // update in the background
                                void this.load();
                            }
                        }}
                    >
                        <ptc-ui-icon name="icon.action.recalculate"></ptc-ui-icon> Konten Neu Berechnen
                    </button>
                </ptc-popover>
            </div>

            <div class="scroller">
                <div class="double-padded">
                    <div>
                        <table class="fill-horizontally">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th scope="col">Übertrag</th>
                                    <th scope="col">Soll</th>
                                    <th scope="col">Ist</th>
                                    <th scope="col">Differenz</th>
                                    <th scope="col">Saldo</th>
                                </tr>
                            </thead>

                            <tbody>
                                ${this._renderBalances(this._balances)}
                            </tbody>
                        </table>
                    </div>

                    <div
                        class="fullbleed center-aligning center-justifying vertical layout scrim"
                        ?hidden=${!this._loading}
                    >
                        <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                    </div>
                </div>
            </div>
        `;
    }
}
